<template>
  <div>
    <div :class="isBabyLanding ? 'baby-landing' : 'wedding-landing'">
      <div class="registry-landing-page-view-container margin-bottom-s">
        <div class="grid-x imageContainer">
          <div class="imageContainerBackground">
            <RegistryUiLandingHero
              variant="banner"
              :desktop-src="babyLandingImageDesktop"
              :mobile-src="babyLandingImageMobile"
              :desktop-placeholder="landingPlaceholderDesktop"
              :mobile-placeholder="landingPlaceholderMobile"
              placeholder-variant="skeleton"
              :width="1440"
              :height="595"
              :mobile-height="mobileHeroHeight"
              alt="RegistryLandingBackgroundImage"
            />
          </div>

          <div class="imageContainerForeground">
            <div class="registry-landing-page-header align-center text-center margin-bottom-s small-12">
              <div class="tt-ramillas-h2 text-center registry-landing-page-title" :class="avantClass">
                {{ isBabyLanding ? BabyLandingPage.Header : WeddingLandingPage.Header }}
              </div>
              <div class="registry-landing-page-subtitle medium">
                {{ isBabyLanding ? BabyLandingPage.Subheader : WeddingLandingPage.Subheader }}
              </div>
            </div>
            <div class="grid-container registry-grid-container large-padding-bottom-m small-padding-bottom-s">
              <div class="grid-x grid-margin-x align-center">
                <CreateRegistryAccountCard :registry-id="registryId" :registries="registriesByOccasion" :class="{ babyModal: boxOpacityEnabled }" />
                <ExistingRegistrySearchCard :class="{ babyModal: boxOpacityEnabled }" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="grid-x cell align-center text-center large-margin-bottom-xxl small-margin-bottom-xl margin-top-s">
        <div class="medium margin-right-xxs">Created a registry in store?</div>
        <div>
          <button class="medium link-registry-entry-point" @click="linkRegistry">Start here</button>
        </div>
      </div>
    </div>
    <LazyRegistryUiProsWrapper
      v-if="isMcom"
      :popular-items="true"
      :product-id="-9"
      :is-landing="true"
      :is-baby-landing="isBabyLanding"
      class="padding-bottom-xl"
      hydrate-on-visible
    />
    <LazyCanvasAemCanvasMain :canvas="canvas" hydrate-on-visible />
    <LazyRegistryUiProsWrapper
      v-if="isBcom"
      :popular-items="true"
      :product-id="-9"
      :is-landing="true"
      :is-baby-landing="isBabyLanding"
      class="padding-bottom-xl"
      hydrate-on-visible
    />
  </div>
</template>

<script setup>
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { Cookie } from '@nuxt-common/util/app/utils/cookie';
import { breakpoints } from '~/config/breakpoints';
import { WeddingLandingPage, BabyLandingPage } from 'config';
import CreateRegistryAccountCard from '~/components/RegistrySignIn/CreateRegistryAccountCard';
import ExistingRegistrySearchCard from '~/components/RegistrySignIn/ExistingRegistrySearchCard';
import { UPDATE_DEFAULT_REGISTRY } from '~/types/actions';
import { SET_REGISTRY_ID_STATUS, SET_REGISTRY_STATUS_OCCASION } from '~/types/mutations';
import { useEnvPropsStore } from '~/stores/envProps';
import { useBaseStore } from '~/stores/base';
import { useCreateRegistryStore } from '~/stores/createRegistry';
import { useAssociation } from '~/composables/useAssociation';

provide('isRegistry', true);
const emits = defineEmits(['toggleDeleteModalOverlay']);

const { registryId, getDefaultRegistry, getRegistriesByOccasion, isManageRegistryExceededForLanding, getUserRegistries } = useAssociation();

const route = useRoute();

const envPropsStore = useEnvPropsStore();
const baseStore = useBaseStore();
const createRegistryStore = useCreateRegistryStore();

const registriesByOccasion = ref([]);

const signedInCookie = useCookie('SignedIn');
const isSignedIn = computed(() => !!Number(signedInCookie.value));
const orgId = computed(() => (envPropsStore.envProps.entryPoint?.toUpperCase() === 'MCOM' ? 12 : 13));
const isMcom = computed(() => orgId.value === 12);
const isBcom = computed(() => orgId.value === 13);

const isBabyLanding = computed(() => route.path.includes('/registry/baby'));
const occasionType = computed(() => (isBabyLanding.value ? 'baby' : 'wedding'));

const isBcomWithBabyLanding = computed(() => isBcom.value && isBabyLanding.value);
const avantClass = computed(() => (isBcomWithBabyLanding.value ? 'aem-bt-secondary-h1 heavy' : 'aem-bt-secondary-h1'));
const boxOpacityEnabled = computed(() => isBcom.value);
const mobileHeroHeight = computed(() => (isMcom.value ? 1040 : 996));

const cdnUrl = computed(() => envPropsStore.envProps?.cdnUrl || '');
const babyLandingImageMobile = computed(
  () => `${cdnUrl.value}/img/${isBabyLanding.value ? BabyLandingPage.imagePathMobileBaby : WeddingLandingPage.imagePathMobile}`,
);
const babyLandingImageDesktop = computed(
  () => `${cdnUrl.value}/img/${isBabyLanding.value ? BabyLandingPage.imagePathDesktopBaby : WeddingLandingPage.imagePathDesktop}`,
);

const landingPlaceholderDesktop = computed(() =>
  isBabyLanding.value ? BabyLandingPage.placeholderDesktopBaby : WeddingLandingPage.placeholderDesktop,
);
const landingPlaceholderMobile = computed(() => (isBabyLanding.value ? BabyLandingPage.placeholderMobileBaby : WeddingLandingPage.placeholderMobile));

const toAvif = (url) => (/\.webp(\?|$)/i.test(url || '') ? url.replace(/\.webp(\?|$)/i, '.avif$1') : null);

const babyLandingImageDesktopAvif = computed(() => toAvif(babyLandingImageDesktop.value));
const babyLandingImageMobileAvif = computed(() => toAvif(babyLandingImageMobile.value));

const pageName = computed(() => (isBabyLanding.value ? 'baby registry landing' : 'wedding registry landing'));

useHead(() => ({
  link: [
    { rel: 'preconnect', href: cdnUrl.value, crossorigin: 'anonymous' },
    {
      rel: 'preload',
      as: 'image',
      type: 'image/avif',
      href: babyLandingImageDesktopAvif.value,
      media: `(min-width: ${breakpoints.large}px)`,
      fetchpriority: 'high',
    },
    {
      rel: 'preload',
      as: 'image',
      type: 'image/avif',
      href: babyLandingImageMobileAvif.value,
      media: `(max-width: ${breakpoints.large - 1}px)`,
      fetchpriority: 'high',
    },
  ],
}));

const canvasPage = computed(() => (isBabyLanding.value ? 'babyRegistryLanding' : 'weddingRegistryLanding'));
const deviceType = computed(() => (envPropsStore.envProps?.isMew ? 'PHONE' : 'DESKTOP'));

const { data: canvas } = useLazyAsyncData(
  `registry-landing-canvas-${canvasPage.value}`,
  () =>
    $fetch('/xapi/homepage/v1/canvas', {
      query: {
        page: canvasPage.value,
        _shoppingMode: 'WEDDING_REGISTRY',
        _deviceType: deviceType.value,
      },
    }),
  { server: false, default: () => ({}) },
);

const { data: userRegistriesData } = useLazyAsyncData(
  'user-registries',
  async () => {
    if (isSignedIn.value) {
      await getUserRegistries(true);
      const registry = getDefaultRegistry(occasionType.value);
      return { registry };
    }
    return { registry: {} };
  },
  { server: false, default: () => ({ registry: {} }) },
);

watch(
  () => userRegistriesData.value,
  async (newData) => {
    if (!newData?.registry) return;

    const registry = newData.registry;

    if (isSignedIn.value && registry?.registryGUID && isManageRegistryExceededForLanding(occasionType.value)) {
      await baseStore[UPDATE_DEFAULT_REGISTRY]({
        orgId: orgId.value,
        registryGUID: registry.registryGUID,
      });
      navigateTo('/registry/manage');
      createRegistryStore[SET_REGISTRY_STATUS_OCCASION](occasionType.value);
      createRegistryStore[SET_REGISTRY_ID_STATUS](true);
      emits('toggleDeleteModalOverlay');
    } else if (isSignedIn.value && isBabyLanding.value) {
      registriesByOccasion.value = getRegistriesByOccasion(occasionType.value);
    }
  },
  { deep: true },
);

const linkRegistry = () => {
  const linkRegistryUrl = registryId.value ? '/registry/manage' : '/registry/link';
  Cookie.set('FORWARDPAGE_KEY', linkRegistryUrl);
  if (isSignedIn.value) {
    navigateTo('/registry/link');
  } else {
    window.location.assign('/registry/link');
  }
};

onMounted(() => {
  const fireAnalyticTag = () => {
    fireTag('view', {
      page_name: pageName.value,
      page_type: 'registry',
    });
  };

  const fireAudiencePixel = () => {
    const randomId = crypto.randomUUID();
    new Image().src = `https://pubads.g.doubleclick.net/activity;dc_iu=/22026345640/DFPAudiencePixel;ord=${randomId};dc_seg=6952796602?`;
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(fireAnalyticTag);
    requestIdleCallback(fireAudiencePixel);
  } else {
    setTimeout(fireAnalyticTag, 1000);
    setTimeout(fireAudiencePixel, 2000);
  }
});
</script>

<style lang="scss" brand="mcom" scoped>
@import '~/assets/scss/views/registryLandingPageViewStyles.scss';
@include custom-font-setup('tt-ramillas');
.babyModal {
  opacity: 85%;
}
</style>

<style lang="scss" brand="bcom" scoped>
@import '~/assets/scss/views/registryLandingPageViewStyles.scss';
.babyModal {
  opacity: 85%;
}

.registry-landing-page-view-container {
  @include breakpoint(small down) {
    margin-top: 0 !important;
  }
}

.force-header-lowercase {
  text-transform: lowercase !important;
}
.baby-landing {
  .registry-landing-page-title {
    @include breakpoint(small only) {
      margin-top: rem-calc(16);
      margin-bottom: 0 !important;
    }

    @include breakpoint(medium up) {
      margin-top: rem-calc(16);
      margin-bottom: 0 !important;
    }
  }
}
</style>
